import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import type { ReleaseDenyReason } from '@/types/release';

/**
 * Reasons that freeze content (rulebook `edit` denials). Shown as a read-only banner in the
 * composers: the content stays visible, nothing is blanked, the reason says why.
 */
const LOCK_REASON_ERROR_KEYS: Partial<Record<ReleaseDenyReason, string>> = {
  frozen_in_review: 'errors:release.frozen_in_review',
  frozen_released: 'errors:release.frozen_released',
  frozen_committed: 'errors:release.frozen_committed',
  not_seed_owner: 'errors:release.not_seed_owner',
};

interface ReleaseLockBannerProps {
  /** The rulebook's deny reason for `edit` (server verdict, or the client mirror's best guess). */
  reason: ReleaseDenyReason | null | undefined;
  className?: string;
}

/** Localised one-line explanation of why content is read-only (shared by PostCreator + VariationCreator). */
export const useReleaseLockText = (): ((reason: ReleaseDenyReason | null | undefined) => string) => {
  const { t, i18n } = useTranslation(['common', 'errors']);
  return (reason) => {
    if (reason === 'not_creator' || !reason) {
      return t('common:release_composer_locked_not_creator');
    }
    const key = LOCK_REASON_ERROR_KEYS[reason];
    return key ? i18n.t(key, { keySeparator: false }) : t('common:release_composer_locked_not_creator');
  };
};

/**
 * Shared "this content is read only" banner of the composers. Renders nothing without a reason.
 */
export const ReleaseLockBanner = ({ reason, className }: ReleaseLockBannerProps) => {
  const { t } = useTranslation('common');
  const lockText = useReleaseLockText();
  if (!reason) return null;

  return (
    <div
      role="status"
      data-testid="release-lock-banner"
      className={`flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 ${className ?? ''}`}
    >
      <Icon name={ICONS.LOCK} size="sm" color="text-amber-700" className="flex-shrink-0 mt-0.5" ariaHidden />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-amber-900">{t('release_composer_locked_title')}</p>
        <p className="text-sm text-amber-800">{lockText(reason)}</p>
      </div>
    </div>
  );
};
